export type Edge = 'flat' | 'in' | 'out';
export type EdgeInfo = { type: Edge; variant: number };

export type PieceEdges = {
  top: EdgeInfo;
  right: EdgeInfo;
  bottom: EdgeInfo;
  left: EdgeInfo;
};

export type EdgesGrid = PieceEdges[];

function createRng(seed: number): () => number {
  let state = (seed >>> 0) || 1;
  return () => {
    state ^= state << 13;
    state ^= state >>> 17;
    state ^= state << 5;
    return ((state >>> 0) & 0xffffffff) / 0x100000000;
  };
}

function randomInt(rng: () => number, min: number, max: number): number {
  return Math.floor(rng() * (max - min + 1)) + min;
}

function randomEdgeDirection(rng: () => number): 'in' | 'out' {
  return rng() < 0.5 ? 'in' : 'out';
}

function opposite(edge: 'in' | 'out'): 'in' | 'out' {
  return edge === 'in' ? 'out' : 'in';
}

function flatEdge(): EdgeInfo {
  return { type: 'flat', variant: 0 };
}

function idx(row: number, col: number, cols: number): number {
  return row * cols + col;
}

export function generateEdgesGrid(rows: number, cols: number, seed: number): EdgesGrid {
  const total = Math.max(0, rows * cols);
  const grid: EdgesGrid = Array.from({ length: total }, () => ({
    top: flatEdge(),
    right: flatEdge(),
    bottom: flatEdge(),
    left: flatEdge(),
  }));
  if (rows <= 0 || cols <= 0) return grid;

  const rng = createRng(seed);

  // Vertical seams: piece(r,c).right paired with piece(r,c+1).left
  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols - 1; col++) {
      const variant = randomInt(rng, 0, 9);
      const dir = randomEdgeDirection(rng);
      const current = idx(row, col, cols);
      const neighbor = idx(row, col + 1, cols);
      grid[current].right = { type: dir, variant };
      grid[neighbor].left = { type: opposite(dir), variant };
    }
  }

  // Horizontal seams: piece(r,c).bottom paired with piece(r+1,c).top
  for (let row = 0; row < rows - 1; row++) {
    for (let col = 0; col < cols; col++) {
      const variant = randomInt(rng, 0, 9);
      const dir = randomEdgeDirection(rng);
      const current = idx(row, col, cols);
      const neighbor = idx(row + 1, col, cols);
      grid[current].bottom = { type: dir, variant };
      grid[neighbor].top = { type: opposite(dir), variant };
    }
  }

  return grid;
}
